// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable } from '@angular/core';
import { ChromosomeUtil } from '../../utils';
import { HceView } from '../../../../../interfaces/hce/hce-reference';

@Injectable({ providedIn: 'root' })
export class HceViewParserService {
  private readonly BASE_RATE = 1_000_000;
  private readonly stainColorMap: Record<string,string> = {
    gneg: 'rgb(255,255,255)',
    gpos: 'rgb(0,0,0)',
    acen: 'rgb(217,47,39)',
    gvar: 'rgb(220,220,220)',
    gpos100: 'rgb(0,0,0)',
    gpos75: 'rgb(130,130,130)',
    gpos66: 'rgb(160,160,160)',
    gpos50: 'rgb(200,200,200)',
    gpos33: 'rgb(210,210,210)',
    gpos25: 'rgb(200,200,200)',
    stalk: 'rgb(100,127,164)',
  };
  private readonly LC_OPTIONS: Intl.CollatorOptions = { numeric: true, sensitivity: 'base' };

  parse(input: HceView) {
    return {
      start: this.bpToMb(input.start),
      end: this.bpToMb(input.end),
      name: input.name,
      gieStain: input.gieStain,
      color: this.toColor(input.gieStain),
    };
  }

  toChromosomeList(records: Array<{ title?: string }>): string[] {
    const titles = records.map(r => r.title).map(t => (t ?? '').split('_')[0]);
    return [...new Set(titles)]
      .filter(chrName => chrName !== 'chrUn')
      .sort(this.compareChromosomes.bind(this));
  }

  private bpToMb(input: string): number {
    const n = Number(input);
    return Number.isFinite(n) ? n / this.BASE_RATE : 0;
  }

  private toColor(gieStain: string): string {
    const hexColor = gieStain.match(/^#([0-9a-fA-F]{6}|[0-9a-fA-F]{3})$/);
    if (hexColor) return gieStain;
    return this.stainColorMap[gieStain] ?? 'rgb(221,221,221)';
  }

  private chromosomeKey(s: string): number {
    const id = ChromosomeUtil.toId(s);
    return Number.isFinite(id as number) ? (id as number) : Number.POSITIVE_INFINITY;
  }

  private compareChromosomes(a: string, b: string): number {
    const ka = this.chromosomeKey(a);
    const kb = this.chromosomeKey(b);
    if (ka !== kb) return ka - kb;
    return a.localeCompare(b, 'en', this.LC_OPTIONS);
  }
}